// askSmart-ui/src/pages/TOSP/components/TospTrafficLights.tsx
import React, { useMemo, useState } from "react";
import { useTospData } from "../TospDataContext";
import { useStoredSales } from "../portal/tenantSales";
import {
  redGroups,
  taskCount,
  TrafficGroup,
  TrafficLevel,
  yellowGroups,
} from "../tospTraffic";
import type { TospTab } from "../ManageTOSP";

interface TospTrafficLightsProps {
  /** Opens the tab a task is fixed on, and closes the panel behind it. */
  onGoTo: (tab: TospTab, focus?: string) => void;
}

const TITLES: Record<TrafficLevel, string> = {
  red: "Needs Admin Action",
  yellow: "Overdue From Tenants",
  green: "Nothing Here Yet",
};

const EMPTY: Record<TrafficLevel, string> = {
  red: "Nothing outstanding — configuration is complete and every tenant record is filled in.",
  yellow: "Every tenant is up to date on their sales reporting.",
  // Green has no rule against it yet. Said plainly rather than shown as a
  // count of zero, which would read as "nothing qualifies today" and leave an
  // admin waiting for something that can't arrive.
  green: "Nothing is reported here yet.",
};

// The three lights above the tabs, and the panel behind them.
//
// Both counts are worked out from records the page already holds, so they are
// never stale and there is nothing to mark read: a light goes out when the
// thing behind it is actually done.
export default function TospTrafficLights({ onGoTo }: TospTrafficLightsProps) {
  const { configuration, tenants, taxRolls, units } = useTospData();
  const { sales, loading } = useStoredSales();
  const [open, setOpen] = useState<TrafficLevel | null>(null);

  // The register and the units are counted here too, so the light and the tab
  // counters are reading one set of rules rather than two that drift apart.
  const red = useMemo(
    () => redGroups(configuration, tenants, taxRolls, units),
    [configuration, tenants, taxRolls, units],
  );
  // Anchored on the day rather than the moment: a month is late by whole days,
  // so a fresh Date() on every render would recompute the same answer forever.
  const today = new Date().toDateString();
  const yellow = useMemo(
    () => yellowGroups(tenants, sales, new Date(today)),
    [tenants, sales, today],
  );

  const counts: Record<TrafficLevel, number> = {
    red: taskCount(red),
    // Unknown until the sales have loaded. Shown as a dash rather than 0,
    // which would say "nobody is behind" of a list nobody has read yet.
    yellow: taskCount(yellow),
    green: 0,
  };

  const groupsFor = (level: TrafficLevel): TrafficGroup[] =>
    level === "red" ? red : level === "yellow" ? yellow : [];

  const light = (level: TrafficLevel, lamp: string) => (
    <button
      type="button"
      className={`tosp-traffic-light tosp-traffic-${level}`}
      title={TITLES[level]}
      aria-label={`${TITLES[level]} — ${counts[level]}`}
      onClick={() => setOpen(level)}
    >
      <span className="tosp-traffic-lamp">{lamp}</span>
      <span className="tosp-traffic-count">
        {level === "yellow" && loading ? "—" : counts[level]}
      </span>
    </button>
  );

  return (
    <>
      <div className="tosp-traffic-bar">
        {light("red", "🔴")}
        {light("yellow", "🟡")}
        {light("green", "🟢")}
      </div>

      {open && (
        <TrafficPanel
          level={open}
          groups={groupsFor(open)}
          onClose={() => setOpen(null)}
          onGoTo={(tab, focus) => {
            setOpen(null);
            onGoTo(tab, focus);
          }}
        />
      )}
    </>
  );
}

interface TrafficPanelProps {
  level: TrafficLevel;
  groups: TrafficGroup[];
  onClose: () => void;
  onGoTo: (tab: TospTab, focus?: string) => void;
}

// The list behind one light. Grouped by what the tasks hang off — Configuration
// or a tenant — and expanded by default: the point of opening it is to read the
// tasks, not to be shown a set of headings to click.
function TrafficPanel({ level, groups, onClose, onGoTo }: TrafficPanelProps) {
  const [collapsed, setCollapsed] = useState<string[]>([]);

  const toggle = (id: string) =>
    setCollapsed((prev) =>
      prev.includes(id) ? prev.filter((each) => each !== id) : [...prev, id],
    );

  return (
    <div className="smrtdocs-modal-overlay" onClick={onClose}>
      <div
        className="smrtdocs-modal tosp-traffic-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="smrtdocs-modal-header">
          <h2 className="tosp-traffic-modal-title">{TITLES[level]}</h2>
          <button type="button" className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="smrtdocs-modal-body tosp-traffic-body">
          {groups.length === 0 ? (
            <p className="tosp-traffic-empty">{EMPTY[level]}</p>
          ) : (
            groups.map((group) => {
              const shut = collapsed.includes(group.id);
              return (
                <div key={group.id} className="tosp-traffic-group">
                  <button
                    type="button"
                    className="tosp-traffic-group-head"
                    aria-expanded={!shut}
                    onClick={() => toggle(group.id)}
                  >
                    <span className="tosp-traffic-group-name">{group.name}</span>
                    <span className={`tosp-traffic-group-count tosp-traffic-${level}-text`}>
                      {group.tasks.length} item{group.tasks.length === 1 ? "" : "s"}
                    </span>
                    <span className="tosp-traffic-chevron">{shut ? "▼" : "▲"}</span>
                  </button>

                  {!shut && (
                    <ul className="tosp-traffic-tasks">
                      {group.tasks.map((task) => (
                        <li key={task.id} className="tosp-traffic-task">
                          <span className={`tosp-traffic-dot tosp-traffic-${level}-dot`} />
                          <span className="tosp-traffic-detail">{task.detail}</span>
                          {/* Straight to where it's fixed. The tab, not the
                              record: opening a tenant's form from here would
                              leave the admin with a modal on top of a modal
                              and nowhere obvious to go back to. */}
                          <button
                            type="button"
                            className="tosp-link-btn"
                            onClick={() => onGoTo(task.tab, task.focus)}
                          >
                            Go
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
